import { CliError, EXIT_INPUT } from "../../../common.js";
import { findEpisodeV4, findSceneV4 } from "../../script/v4/address.js";
import type {
  ActionV4,
  ActorV4,
  EpisodeV4,
  LocationV4,
  PropV4,
  SceneV4,
  ScriptV4,
} from "../../script/v4/types.js";
import { sortDeep } from "../../script/text-util.js";
import { strictlyReducesValidationErrors } from "../../script/shared/validation.js";
import { validateScriptV4 } from "../../script/v4/validation.js";
import type {
  V4AuthoringDocument,
  V4AuthoringTarget,
  V4Declaration,
} from "./types.js";

export interface V4AuthoringTargetResult {
  kind: V4AuthoringTarget["kind"];
  address: string;
  before: { episodes: number; scenes: number; actions: number };
  after: { episodes: number; scenes: number; actions: number };
  retainedIds: string[];
  createdIds: string[];
  removedIds: string[];
  untouchedSiblings: number;
}

export interface V4AuthoringApplyResult {
  script: ScriptV4;
  targets: V4AuthoringTargetResult[];
  changed: boolean;
  declarations: {
    createdAssets: string[];
    existingAssets: string[];
    createdStates: string[];
    existingStates: string[];
  };
  retainedOrphanAssets: string[];
  identity: {
    createdIds: string[];
    removedIds: string[];
    retainedIds: string[];
  };
  validation: ReturnType<typeof validateScriptV4>;
}

function assertValidOrImproved(
  current: ScriptV4 | null,
  validation: ReturnType<typeof validateScriptV4>,
): void {
  if (
    validation.ok
    || (current !== null
      && strictlyReducesValidationErrors(validateScriptV4(current), validation))
  ) return;
  throw new CliError("The proposed document is not a valid or strictly improved Script v4.", {
          exitCode: EXIT_INPUT,
          errorCode: "AUTHORING_VALIDATION_FAILED",
          data: { actual: validation.errors.slice(0, 20).map((issue) => `${issue.path}: ${issue.message}`) },
        });
}

function blocked(message: string, received: string[]): never {
  throw new CliError(message, {
    exitCode: EXIT_INPUT,
    errorCode: "AUTHORING_APPLY_INVALID",
    data: { actual: received },
  });
}

interface StructuralSummary {
  ids: string[];
  counts: { episodes: number; scenes: number; actions: number };
}

interface CurrentTargetSummary extends StructuralSummary {
  siblings: number;
}

function summarizeActions(actions: readonly ActionV4[]): StructuralSummary {
  return {
    ids: actions.map((action) => action.action_id),
    counts: { episodes: 0, scenes: 0, actions: actions.length },
  };
}

function summarizeScenes(scenes: readonly SceneV4[]): StructuralSummary {
  return {
    ids: scenes.flatMap((scene) => [
      scene.scene_id,
      ...scene.actions.map((action) => action.action_id),
    ]),
    counts: {
      episodes: 0,
      scenes: scenes.length,
      actions: scenes.reduce((sum, scene) => sum + scene.actions.length, 0),
    },
  };
}

function summarizeEpisodes(episodes: readonly EpisodeV4[]): StructuralSummary {
  return {
    ids: episodes.flatMap((episode) => [
      episode.episode_id,
      ...episode.scenes.flatMap((scene) => [
        scene.scene_id,
        ...scene.actions.map((action) => action.action_id),
      ]),
    ]),
    counts: {
      episodes: episodes.length,
      scenes: episodes.reduce((sum, episode) => sum + episode.scenes.length, 0),
      actions: episodes.reduce(
        (sum, episode) => sum + episode.scenes.reduce(
          (sceneSum, scene) => sceneSum + scene.actions.length,
          0,
        ),
        0,
      ),
    },
  };
}

function authoredTargetSummary(target: V4AuthoringTarget): StructuralSummary {
  if (target.kind === "script") return summarizeEpisodes(target.value.episodes);
  if (target.kind === "episode") return summarizeEpisodes([target.value]);
  if (target.kind === "episode-span") return summarizeEpisodes(target.values);
  if (target.kind === "scene") return summarizeScenes([target.value]);
  if (target.kind === "scene-span") return summarizeScenes(target.values);
  return summarizeActions(target.values);
}

function assetsFromDeclarations(declarations: V4Declaration[]): {
  actors: ActorV4[];
  locations: LocationV4[];
  props: PropV4[];
} {
  return {
    actors: declarations.filter((item): item is Extract<V4Declaration, { kind: "actor" }> => item.kind === "actor")
      .map((item) => structuredClone(item.value)),
    locations: declarations.filter((item): item is Extract<V4Declaration, { kind: "location" }> => item.kind === "location")
      .map((item) => structuredClone(item.value)),
    props: declarations.filter((item): item is Extract<V4Declaration, { kind: "prop" }> => item.kind === "prop")
      .map((item) => structuredClone(item.value)),
  };
}

function semanticEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(sortDeep(left)) === JSON.stringify(sortDeep(right));
}

function declarationPlan(
  current: ScriptV4 | null,
  declarations: V4Declaration[],
): V4AuthoringApplyResult["declarations"] {
  const currentAssets = new Set<string>(current === null ? [] : [
    ...current.actors.map((asset) => asset.actor_id),
    ...current.locations.map((asset) => asset.location_id),
    ...current.props.map((asset) => asset.prop_id),
  ]);
  const currentStates = new Set<string>(current === null ? [] : [
    ...current.actors.flatMap((asset) =>
      asset.states.map((state) => `${asset.actor_id}/${state.state_id}`)),
    ...current.locations.flatMap((asset) =>
      asset.states.map((state) => `${asset.location_id}/${state.state_id}`)),
    ...current.props.flatMap((asset) =>
      asset.states.map((state) => `${asset.prop_id}/${state.state_id}`)),
  ]);
  const createdAssets: string[] = [];
  const existingAssets: string[] = [];
  const createdStates: string[] = [];
  const existingStates: string[] = [];
  const classifyState = (address: string): void => {
    (currentStates.has(address) ? existingStates : createdStates).push(address);
  };
  for (const declaration of declarations) {
    if (declaration.kind === "state") {
      if (currentAssets.has(declaration.ownerId)) existingAssets.push(declaration.ownerId);
      classifyState(`${declaration.ownerId}/${declaration.value.state_id}`);
      continue;
    }
    const id = declaration.kind === "actor"
      ? declaration.value.actor_id
      : declaration.kind === "location"
        ? declaration.value.location_id
        : declaration.value.prop_id;
    (currentAssets.has(id) ? existingAssets : createdAssets).push(id);
    declaration.value.states.forEach((state) => {
      classifyState(`${id}/${state.state_id}`);
    });
  }
  return {
    createdAssets,
    existingAssets: [...new Set(existingAssets)],
    createdStates,
    existingStates,
  };
}

function orphanAssets(script: ScriptV4): string[] {
  const referenced = new Set<string>();
  for (const episode of script.episodes) {
    for (const scene of episode.scenes) {
      if (scene.location) referenced.add(scene.location.location_id);
      scene.actors.forEach((ref) => referenced.add(ref.actor_id));
      scene.props.forEach((ref) => referenced.add(ref.prop_id));
      scene.actions.forEach((action) => {
        if (action.transition_prompt) referenced.add(action.transition_prompt.target);
      });
    }
  }
  return [
    ...script.actors.map((asset) => asset.actor_id),
    ...script.locations.map((asset) => asset.location_id),
    ...script.props.map((asset) => asset.prop_id),
  ].filter((id) => !referenced.has(id));
}

function canonicalIds(script: ScriptV4): string[] {
  return [
    ...script.actors.flatMap((asset) => [
      asset.actor_id,
      ...asset.states.map((state) => `${asset.actor_id}/${state.state_id}`),
    ]),
    ...script.locations.flatMap((asset) => [
      asset.location_id,
      ...asset.states.map((state) => `${asset.location_id}/${state.state_id}`),
    ]),
    ...script.props.flatMap((asset) => [
      asset.prop_id,
      ...asset.states.map((state) => `${asset.prop_id}/${state.state_id}`),
    ]),
    ...script.episodes.flatMap((episode) => [
      episode.episode_id,
      ...episode.scenes.flatMap((scene) => [
        scene.scene_id,
        ...scene.actions.map((action) => action.action_id),
      ]),
    ]),
  ];
}

function identityChanges(
  before: ScriptV4 | null,
  after: ScriptV4,
): V4AuthoringApplyResult["identity"] {
  const beforeIds = before === null ? [] : canonicalIds(before);
  const afterIds = canonicalIds(after);
  const beforeSet = new Set(beforeIds);
  const afterSet = new Set(afterIds);
  return {
    createdIds: afterIds.filter((id) => !beforeSet.has(id)),
    removedIds: beforeIds.filter((id) => !afterSet.has(id)),
    retainedIds: beforeIds.filter((id) => afterSet.has(id)),
  };
}

function addDeclarations(script: ScriptV4, declarations: V4Declaration[]): void {
  for (const declaration of declarations) {
    if (declaration.kind === "actor") {
      if (script.actors.some((asset) => asset.actor_id === declaration.value.actor_id)) {
        blocked("Declaration cannot redefine an existing actor.", [declaration.value.actor_id]);
      }
      script.actors.push(structuredClone(declaration.value));
    } else if (declaration.kind === "location") {
      if (script.locations.some((asset) => asset.location_id === declaration.value.location_id)) {
        blocked("Declaration cannot redefine an existing location.", [declaration.value.location_id]);
      }
      script.locations.push(structuredClone(declaration.value));
    } else if (declaration.kind === "prop") {
      if (script.props.some((asset) => asset.prop_id === declaration.value.prop_id)) {
        blocked("Declaration cannot redefine an existing prop.", [declaration.value.prop_id]);
      }
      script.props.push(structuredClone(declaration.value));
    } else {
      const owner = script.actors.find((asset) => asset.actor_id === declaration.ownerId)
        ?? script.locations.find((asset) => asset.location_id === declaration.ownerId)
        ?? script.props.find((asset) => asset.prop_id === declaration.ownerId);
      if (!owner) blocked("State declaration owner was not found.", [declaration.ownerId]);
      if (owner.states.some((state) => state.state_id === declaration.value.state_id)) {
        blocked("State declaration cannot redefine an existing state.", [
          `${declaration.ownerId}/${declaration.value.state_id}`,
        ]);
      }
      owner.states.push(structuredClone(declaration.value));
    }
  }
}

function endpoints(ids: readonly string[], from: string, through: string): [number, number] {
  const left = ids.indexOf(from);
  const right = ids.indexOf(through);
  if (left < 0 || right < 0) blocked("Span endpoint was not found.", [from, through]);
  return [Math.min(left, right), Math.max(left, right)];
}

function targetInterval(script: ScriptV4, target: V4AuthoringTarget): [number, number] {
  if (target.kind === "script") return [0, 0];
  if (target.kind === "episode" || target.kind === "episode-span") {
    const ids = script.episodes.map((episode) => episode.episode_id);
    return target.kind === "episode"
      ? endpoints(ids, target.value.episode_id, target.value.episode_id)
      : endpoints(ids, target.from, target.through);
  }
  if (target.kind === "scene" || target.kind === "scene-span") {
    const ids = script.episodes.flatMap((episode) => episode.scenes.map((scene) => scene.scene_id));
    return target.kind === "scene"
      ? endpoints(ids, target.value.scene_id, target.value.scene_id)
      : endpoints(ids, target.from, target.through);
  }
  const ids = script.episodes.flatMap((episode) =>
    episode.scenes.flatMap((scene) => scene.actions.map((action) => action.action_id)));
  return endpoints(ids, target.from, target.through);
}

function validateReplaceBatch(script: ScriptV4, targets: V4AuthoringTarget[]): void {
  let previousEnd = -1;
  for (const target of targets) {
    const [start, end] = targetInterval(script, target);
    if (start <= previousEnd) {
      blocked("Replace targets must follow current document order without overlap.", [
        targetAddress(target),
      ]);
    }
    previousEnd = end;
  }
}

function targetAddress(target: V4AuthoringTarget): string {
  if (target.kind === "script") return "script";
  if (target.kind === "episode") return target.value.episode_id;
  if (target.kind === "scene") return target.value.scene_id;
  return `${target.from}..${target.through}`;
}

function currentTargetSummary(script: ScriptV4, target: V4AuthoringTarget): CurrentTargetSummary {
  if (target.kind === "script") {
    return { ...summarizeEpisodes(script.episodes), siblings: 0 };
  }
  if (target.kind === "episode") {
    const found = findEpisodeV4(script, target.value.episode_id);
    return {
      ...(found ? summarizeEpisodes([found.episode]) : summarizeEpisodes([])),
      siblings: Math.max(0, script.episodes.length - (found ? 1 : 0)),
    };
  }
  if (target.kind === "episode-span") {
    const [start, end] = endpoints(script.episodes.map((episode) => episode.episode_id), target.from, target.through);
    return {
      ...summarizeEpisodes(script.episodes.slice(start, end + 1)),
      siblings: script.episodes.length - (end - start + 1),
    };
  }
  if (target.kind === "scene") {
    const found = findSceneV4(script, target.value.scene_id);
    const parent = findEpisodeV4(script, target.parent)?.episode;
    return {
      ...(found ? summarizeScenes([found.scene]) : summarizeScenes([])),
      siblings: found
        ? Math.max(0, found.episode.scenes.length - 1)
        : parent?.scenes.length ?? 0,
    };
  }
  if (target.kind === "scene-span") {
    const episode = findEpisodeV4(script, target.parent)?.episode;
    if (!episode) return { ...summarizeScenes([]), siblings: 0 };
    const [start, end] = endpoints(episode.scenes.map((scene) => scene.scene_id), target.from, target.through);
    return {
      ...summarizeScenes(episode.scenes.slice(start, end + 1)),
      siblings: episode.scenes.length - (end - start + 1),
    };
  }
  const scene = findSceneV4(script, target.parent)?.scene;
  if (!scene) return { ...summarizeActions([]), siblings: 0 };
  const [start, end] = endpoints(scene.actions.map((action) => action.action_id), target.from, target.through);
  return {
    ...summarizeActions(scene.actions.slice(start, end + 1)),
    siblings: scene.actions.length - (end - start + 1),
  };
}

function targetResult(
  target: V4AuthoringTarget,
  before: CurrentTargetSummary,
): V4AuthoringTargetResult {
  const after = authoredTargetSummary(target);
  const beforeSet = new Set(before.ids);
  const afterSet = new Set(after.ids);
  return {
    kind: target.kind,
    address: targetAddress(target),
    before: before.counts,
    after: after.counts,
    retainedIds: before.ids.filter((id) => afterSet.has(id)),
    createdIds: after.ids.filter((id) => !beforeSet.has(id)),
    removedIds: before.ids.filter((id) => !afterSet.has(id)),
    untouchedSiblings: before.siblings,
  };
}

function replaceTarget(script: ScriptV4, target: V4AuthoringTarget): void {
  if (target.kind === "script") blocked("An existing Script v4 is not replaced through a script root.", ["script"]);
  if (target.kind === "episode") {
    const found = findEpisodeV4(script, target.value.episode_id);
    if (!found) blocked("Episode replacement target was not found.", [target.value.episode_id]);
    script.episodes[found.episodeIndex] = structuredClone(target.value);
  } else if (target.kind === "episode-span") {
    const [start, end] = endpoints(script.episodes.map((episode) => episode.episode_id), target.from, target.through);
    if (target.values.length === 0) blocked("Episode span replacement cannot be empty.", [target.from, target.through]);
    script.episodes.splice(start, end - start + 1, ...structuredClone(target.values));
  } else if (target.kind === "scene") {
    const found = findSceneV4(script, target.value.scene_id);
    if (!found || found.episode.episode_id !== target.parent) {
      blocked("Scene replacement target was not found under its parent.", [target.parent, target.value.scene_id]);
    }
    found.episode.scenes[found.sceneIndex] = structuredClone(target.value);
  } else if (target.kind === "scene-span") {
    const episode = findEpisodeV4(script, target.parent)?.episode;
    if (!episode) blocked("Scene span parent was not found.", [target.parent]);
    const [start, end] = endpoints(episode.scenes.map((scene) => scene.scene_id), target.from, target.through);
    if (target.values.length === 0) blocked("Scene span replacement cannot be empty.", [target.from, target.through]);
    episode.scenes.splice(start, end - start + 1, ...structuredClone(target.values));
  } else {
    const scene = findSceneV4(script, target.parent)?.scene;
    if (!scene) blocked("Action span parent was not found.", [target.parent]);
    const [start, end] = endpoints(scene.actions.map((action) => action.action_id), target.from, target.through);
    if (target.values.length === 0) blocked("Action span replacement cannot be empty.", [target.from, target.through]);
    scene.actions.splice(start, end - start + 1, ...structuredClone(target.values));
  }
}

function createTarget(script: ScriptV4, target: V4AuthoringTarget): void {
  if (target.kind === "episode") {
    script.episodes.push(structuredClone(target.value));
  } else if (target.kind === "scene") {
    const episode = findEpisodeV4(script, target.parent)?.episode;
    if (!episode) blocked("Scene create parent was not found.", [target.parent]);
    episode.scenes.push(structuredClone(target.value));
  } else {
    blocked("This target kind does not support create against an existing script.", [target.kind]);
  }
}

function scriptFromCreateDocument(document: V4AuthoringDocument): ScriptV4 {
  if (document.targets.length !== 1 || document.targets[0]!.kind !== "script") {
    return blocked("Script create requires exactly one script target.", [document.targetKind]);
  }
  if (document.declarations.some((item) => item.kind === "state")) {
    return blocked("Standalone state declarations need an existing owner.", ["state"]);
  }
  const target = document.targets[0] as Extract<V4AuthoringTarget, { kind: "script" }>;
  return {
    version: 4,
    ...structuredClone(target.value),
    ...assetsFromDeclarations(document.declarations),
  };
}

export function applyAuthoringV4(
  current: ScriptV4 | null,
  document: V4AuthoringDocument,
  mode: "create" | "replace",
): V4AuthoringApplyResult {
  let script: ScriptV4;
  if (current === null) {
    if (mode !== "create") {
      blocked("A missing target requires one script create target.", [mode, document.targetKind]);
    }
    script = scriptFromCreateDocument(document);
  } else if (mode === "create" && document.targetKind === "script") {
    script = scriptFromCreateDocument(document);
    if (!semanticEqual(script, current)) {
      blocked("Script create retry differs from the existing script.", ["script"]);
    }
  } else {
    script = structuredClone(current);
    if (mode === "replace") validateReplaceBatch(current, document.targets);
    addDeclarations(script, document.declarations);
    const beforeTargets = document.targets.map((target) =>
      currentTargetSummary(current, target));
    if (mode === "replace") {
      for (const target of [...document.targets].reverse()) replaceTarget(script, target);
    } else {
      for (const target of document.targets) createTarget(script, target);
    }
    const targetResults = document.targets.map((target, index) =>
      targetResult(target, beforeTargets[index]!));
    const validation = validateScriptV4(script);
    assertValidOrImproved(current, validation);
    return {
      script,
      changed: !semanticEqual(current, script),
      validation,
      targets: targetResults,
      declarations: declarationPlan(current, document.declarations),
      retainedOrphanAssets: orphanAssets(script),
      identity: identityChanges(current, script),
    };
  }
  const validation = validateScriptV4(script);
  assertValidOrImproved(current, validation);
  return {
    script,
    changed: current === null || !semanticEqual(current, script),
    validation,
    declarations: declarationPlan(current, document.declarations),
    retainedOrphanAssets: orphanAssets(script),
    identity: identityChanges(current, script),
    targets: document.targets.map((target) => {
      const before = current === null
        ? { ...summarizeEpisodes([]), siblings: 0 }
        : currentTargetSummary(current, target);
      return targetResult(target, before);
    }),
  };
}
